import { defineStore } from "pinia";
import { computed, ComputedRef, ref, Ref } from "vue";
import {
    handleErrorResponse,
    handleErrorResponseLegacyMethod,
} from "../helper/api";
import { RemovableRef, StorageSerializers, useSessionStorage } from "@vueuse/core";
import {
    CheckoutStepCreateOrder,
    CheckoutStepUpdateOrder,
    emitEvent,
    EventCartAdd,
    EventCartRemove,
    EventCheckoutStep,
} from "../helper/tracking-events";
import { generateRoute } from "../helper-functions";

const locale = document.documentElement.lang ?? "nl";

export const useCartStore = defineStore("cart", () => {
    let isPopoverOpened: Ref<Boolean> = ref(false);
    let loadingCart: Ref<Boolean> = ref(true);
    let cartInitialized: Ref<Boolean> = ref(false);
    let cart: Ref<Cart> = ref({
        products: [],
        checkout_method: null,
        coupon: null,
        postnl_data: null,
        wrapping_paper: null,
        country: null,
        invoice_address: null,
        shipping_address: null,
        order_id: null,
        total: 0,
    });

    async function initCart() {
        if (cartInitialized.value) {
            return;
        }
        cartInitialized.value = true;
        loadingCart.value = true;

        try {
            await getCart();

            if (cart.value.coupon !== null) {
                couponCode.value = cart.value.coupon.code;
            }

            await getCountries();
            postNLApiOnline.value = await isPostNLOnline();
            await getShippingMethods();
            await getPaymentMethods();

            // Check if the checkout method in the cart is the same as the one in the session storage.
            // If it is not the same, we need to update the checkout method in the session storage.
            const checkoutMethod = cart.value.checkout_method?.ID ?? null
            if (checkoutMethod !== null) {
                if (selectedPayMethod.value?.checkout_method_id != checkoutMethod) {
                    const paymentMethod = paymentMethods.value.find(
                        (payMethod) => payMethod.checkout_method_id == checkoutMethod,
                    );

                    if (paymentMethod !== undefined) {
                        setPaymentMethod(paymentMethod);
                    }
                }
            }

            await getDefaultTimeFrames();
            await initGiftWrapItems();
        } catch (e) {
            console.error("there was an error fetching the cart: ", e);
        }
        loadingCart.value = false;
    }

    let wrappingPaper: Ref<CartWrappingPaper | null> = ref(null);
    let productCount: ComputedRef<number> = computed(() => {
        let count = 0;
        if (cart.value.products.length === 0) {
            return 0;
        }

        cart.value.products.forEach((product) => {
            count += product.quantity;
        });
        return count;
    });

    let giftWrapItems: Ref<CartWrappingPaper[]> = ref([]);
    let checkoutCountries: Ref<CartCountry[]> = ref([]);
    let selectedCountry: Ref<CartCountry | null> = ref(null);
    let shippingMethods: Ref<CartShippingMethod[]> = ref([]);
    let paymentMethods: Ref<CartPaymentMethod[]> = ref([]);
    let selectedPayMethod: RemovableRef<CartPaymentMethod | null> =
        useSessionStorage(
            "cloudcommerce.checkout.selected_pay_method_id",
            null,
            { serializer: StorageSerializers.object },
        );
    let invoiceAddress: RemovableRef<CartAddress> = useSessionStorage(
        "cloudcommerce.checkout.invoice_address",
        emptyAddress(),
    );
    let sameShippingAddress: RemovableRef<boolean> = useSessionStorage(
        "cloudcommerce.checkout.same_shipping_address",
        true,
    );
    let shippingAddress: RemovableRef<CartAddress> = useSessionStorage(
        "cloudcommerce.checkout.shipping_address",
        emptyAddress(),
    );
    let orderComment: RemovableRef<string> = useSessionStorage(
        "cloudcommerce.checkout.order_comment",
        "",
    );
    let termsOfServiceAccepted: RemovableRef<boolean> = useSessionStorage(
        "cloudcommerce.checkout.terms_of_service_accepted",
        false,
    );
    let newsletterAccepted: RemovableRef<boolean> = useSessionStorage(
        "cloudcommerce.checkout.newsletter_accepted",
        false,
    );
    let couponCode: RemovableRef<string> = useSessionStorage(
        "cloudcommerce.checkout.coupon_code",
        "",
    );
    let selectedTimeFrame: Ref<PostnlTimeFrame | null> = ref(null);
    let customerAge: RemovableRef<Date> = useSessionStorage(
        "cloudcommerce.checkout.customer_age",
        new Date(),
    );
    let customerGender: RemovableRef<CustomerGenders> =
        useSessionStorage<CustomerGenders>(
            "cloudcommerce.checkout.customer_gender",
            CustomerGenders.None,
        );
    let customerIban: RemovableRef<string> = useSessionStorage(
        "cloudcommerce.checkout.customer_iban",
        "",
    );
    let postNLApiOnline: Ref<boolean> = ref(true);

    function openCart() {
        isPopoverOpened.value = true;
    }

    function closeCart() {
        isPopoverOpened.value = false;
    }

    async function add(
        productId: string,
        amount: number = 1,
        configurationOf: string | null = null,
    ) {
        loadingCart.value = true;
        isPopoverOpened.value = true;

        let url = `/cart/add?product=${productId}&amount=${amount}`;
        if (configurationOf !== null) {
            url = url + `&configuration_of=${configurationOf}`;
        }

        const response = await fetch(url, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (response.status !== 200) {
            await handleErrorResponse(response);
        }

        await getCart();

        if (amount !== null && amount > 0) {
            const product = cart.value.products.find(
                (product) => product.ID === productId,
            );
            if (product !== undefined) {
                emitEvent(EventCartAdd, productEventData(product));
            }
        }

        loadingCart.value = false;
    }

    function productEventData(product: Product, qty: number | null = null) {
        let attributes: { [key: string]: string } = {};
        for (let [key, attributeValue] of Object.entries(product.attributes)) {
            attributes[key] = attributeValue.value;
        }

        const payload = {
            ID: product.ID,
            name: product.name,
            configurationOf: product.configuration_of,
            price: product.price, // The price in cents, equivalent to €59.99.
            qty: qty !== null ? qty : product.quantity,
            attributes: attributes,
        };

        return payload;
    }

    async function remove(productId: string, amount: number = 1) {
        const product = cart.value.products.find(
            (product) => product.ID === productId,
        );
        if (typeof product === "undefined") {
            // Product is not in current cart so not going to do anything.
            return;
        }

        loadingCart.value = true;

        const response = await fetch(
            `/cart/remove?product=${productId}&amount=${amount}`,
            {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
            },
        );
        if (response.status !== 200) {
            await handleErrorResponse(response);
        }

        // Fetch new cart.
        await getCart();

        if (amount !== null) {
            emitEvent(EventCartRemove, productEventData(product, amount));
        }

        loadingCart.value = false;
    }

    async function getCart() {
        loadingCart.value = true;

        const response = await fetch(`/cart/list?lang=${locale}`, {
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (response.status !== 200) {
            await handleErrorResponse(response);
            loadingCart.value = false;
            return;
        }
        let data = await response.json();
        cart.value = data;
        loadingCart.value = false;
    }

    async function initGiftWrapItems() {
        const response = await fetch(
            `/cart/wrapping_options/list?lang=${locale}`,
            {
                headers: {
                    "Content-Type": "application/json",
                },
            },
        );
        if (response.status !== 200) {
            await handleErrorResponse(response);
            giftWrapItems.value = [];
            return;
        }

        giftWrapItems.value = await response.json();
    }

    async function setWrappingPaper(ID: string) {
        const response = await fetch(
            `/cart/wrapping_options/set/${ID}?lang=${locale}`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
            },
        );
        if (response.status !== 200) {
            await handleErrorResponse(response);
            return;
        }

        await getCart();
    }

    async function removeWrappingPaper() {
        const response = await fetch(
            `/cart/wrapping_options/remove?lang=${locale}`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
            },
        );
        if (response.status !== 200) {
            await handleErrorResponse(response);
            return;
        }

        await getCart();
    }

    async function addCoupon(code: string) {
        const response = await fetch(`/cart/coupon/add?lang=${locale}`, {
            method: "POST",
            body: JSON.stringify({
                code: code,
            }),
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (response.status !== 200) {
            await handleErrorResponse(response);
            return;
        }

        await getCart();
    }

    async function removeCoupon() {
        const response = await fetch(`/cart/coupon/remove?lang=${locale}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (response.status !== 200) {
            await handleErrorResponse(response);
            return;
        }
        couponCode.value = "";
        await getCart();
    }

    async function getCountries() {
        const response = await fetch(`/checkout/countries/get?lang=${locale}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (response.status !== 200) {
            await handleErrorResponse(response);
            return;
        }

        const respBody = await response.json();
        checkoutCountries.value = respBody.map(
            (country: {
                id: string;
                name: string;
                shortCode: string;
                isDefault: boolean;
            }) => {
                return {
                    ID: country.id,
                    name: country.name,
                    shortCode: country.shortCode,
                    is_default: country.isDefault,
                };
            },
        );
    }

    async function setCheckoutCountry(countryID: string) {
        const response = await fetch(
            `/cart/update_country/${countryID}?lang=${locale}`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
            },
        );
        if (response.status !== 200) {
            await handleErrorResponse(response);
            return;
        }

        await getShippingMethods();
        await getCart();
    }

    async function getShippingMethods() {
        const response = await fetch(`/cart/shipping_methods?lang=${locale}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (response.status !== 200) {
            await handleErrorResponse(response);
            return;
        }

        const respBody = await response.json();
        shippingMethods.value = respBody;
    }

    async function getPaymentMethods() {
        if (cart.value.products.length === 0) {
            return;
        }

        // Only load payment methods when we are on a checkout page.
        if (window.location.pathname.indexOf("/checkout") === -1) {
            return;
        }

        loadingCart.value = true;

        const response = await fetch(
            `/checkout/get_payment_methods?lang=${locale}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            },
        );
        if (response.status !== 200) {
            await handleErrorResponse(response);
            loadingCart.value = false;
            return;
        }

        const respBody = await response.json();
        paymentMethods.value = respBody.map(
            (payMethod: {
                internal_name: string;
                name: string;
                checkoutMethod: string;
                validate_age: boolean;
                validate_gender: boolean;
                validate_iban: boolean;
            }) => {
                let cartPaymentMethod: CartPaymentMethod = {
                    name: payMethod.name,
                    internalName: payMethod.internal_name,
                    checkout_method_id: payMethod.checkoutMethod,
                    validateAge: payMethod.validate_age ?? false,
                    validateGender: payMethod.validate_gender ?? false,
                    validateIban: payMethod.validate_iban ?? false,
                };

                return cartPaymentMethod;
            },
        );

        // Set default payment method
        if (
            !selectePayMethodExists(selectedPayMethod.value?.internalName ?? "")
        ) {
            selectDefaultPayMethod();
        }
        loadingCart.value = false;
    }

    async function setCheckoutMethod(checkoutMethod: string) {
        loadingCart.value = true;

        const response = await fetch(
            `/cart/checkout_method/${checkoutMethod}?lang=${locale}`,
            {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
            },
        );
        if (response.status !== 200) {
            await handleErrorResponse(response);

            // Reset state.
            await getShippingMethods();
            await getCart();
            loadingCart.value = false;
            return;
        }

        await getShippingMethods();
        await getCart();

        loadingCart.value = false;
    }

    async function getAddressAutocomplete(
        zipcode: string,
        houseNumber: string,
    ): Promise<CartAddressAutocomplete | null> {
        const response = await fetch(`/checkout/get_address_information`, {
            method: "POST",
            body: JSON.stringify({
                zipcode: zipcode + '',
                house_number: houseNumber + '',
            }),
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (response.status !== 200) {
            return null;
        }

        const respBody = await response.json();
        return respBody as CartAddressAutocomplete;
    }

    async function saveAddresses(): Promise<boolean> {
        const request: {
            address: {
                first_name: string;
                middle_name: string;
                last_name: string;
                street: string;
                house_number: string;
                house_number_ext: string;
                zipcode: string;
                city: string;
                email: string;
                phone: string;
                company_name: string;
            };
            delivery_address: {
                first_name: string;
                middle_name: string;
                last_name: string;
                street: string;
                house_number: string;
                house_number_ext: string;
                zipcode: string;
                city: string;
                email: string;
                phone: string;
                company_name: string;
            } | null;
            use_alternative_address: boolean;
            should_update: boolean;
            order_comment: string;
        } = {
            address: {
                first_name: invoiceAddress.value.first_name,
                middle_name: invoiceAddress.value.middle_name,
                last_name: invoiceAddress.value.last_name,
                street: invoiceAddress.value.street,
                house_number: invoiceAddress.value.house_number.toString(),
                house_number_ext: invoiceAddress.value.house_number_addition,
                zipcode: invoiceAddress.value.zipcode,
                city: invoiceAddress.value.city,
                email: invoiceAddress.value.email,
                phone: invoiceAddress.value.phone_number,
                company_name: invoiceAddress.value.company_name,
            },
            delivery_address: null,
            use_alternative_address: sameShippingAddress.value !== true,
            // No idea why this is needed.
            should_update: true,
            order_comment: orderComment.value,
        };

        if (shippingAddress.value && !sameShippingAddress.value) {
            request.delivery_address = {
                first_name: shippingAddress.value.first_name,
                middle_name: shippingAddress.value.middle_name,
                last_name: shippingAddress.value.last_name,
                street: shippingAddress.value.street,
                house_number: shippingAddress.value.house_number.toString(),
                house_number_ext: shippingAddress.value.house_number_addition,
                zipcode: shippingAddress.value.zipcode,
                city: shippingAddress.value.city,
                company_name: shippingAddress.value.company_name,
                // Use values from the invoice address.
                email: invoiceAddress.value.email,
                phone: invoiceAddress.value.phone_number,
            };
        }

        const response = await fetch(
            `/checkout/save_addresses?lang=${locale}`,
            {
                method: "POST",
                body: JSON.stringify(request),
                headers: {
                    "Content-Type": "application/json",
                },
            },
        );
        if (response.status !== 200) {
            const responseBody = await response.json();
            await handleErrorResponseLegacyMethod(responseBody);
            return false;
        }

        return true;
    }

    function selectePayMethodExists(id: string): boolean {
        return (
            paymentMethods.value.filter(
                (payMethod) => payMethod.internalName === id,
            ).length > 0
        );
    }

    function selectDefaultPayMethod() {
        if (paymentMethods.value.length === 0) {
            return;
        }

        // TODO: Remove this logic in favour of selecting this in the admin backend.
        let payMethod: CartPaymentMethod | null =
            paymentMethods.value.filter(
                (payMethod) => payMethod.internalName.toUpperCase() === "IDEAL",
            )[0] ?? null;
        if (payMethod === null) {
            payMethod = paymentMethods.value[0];
        }

        selectedPayMethod.value = payMethod;
    }

    function emptyAddress(): CartAddress {
        return {
            first_name: "",
            middle_name: "",
            last_name: "",
            street: "",
            house_number: "",
            house_number_addition: "",
            zipcode: "",
            city: "",
            email: "",
            phone_number: "",
            company_name: "",
        } as CartAddress;
    }

    async function createOrder() {
        if (!termsOfServiceAccepted.value) {
            return false;
        }

        if (!selectedPayMethod.value) {
            return false;
        }

        const request: InsertOrderRequest = {
            internal_name: selectedPayMethod.value?.internalName ?? "",
            checkoutMethod: selectedPayMethod.value?.checkout_method_id,
            orderComment: orderComment.value,
            iban: "",
            riverty: null,
            subscribeNewsletter: newsletterAccepted.value,
        };

        if (selectedPayMethod.value?.validateIban) {
            request.iban = customerIban.value;
        }

        if (selectedPayMethod.value?.validateAge) {
            request.riverty = {
                dobDay: customerAge.value.getDate(),
                dobMonth: customerAge.value.getMonth() + 1,
                dobYear: customerAge.value.getFullYear(),
                gender: "none",
            };
        }

        if (
            selectedPayMethod.value?.validateGender &&
            (CustomerGenders.Male === customerGender.value ||
                CustomerGenders.Female === customerGender.value)
        ) {
            if (request.riverty === null) {
                request.riverty = {
                    dobDay: new Date().getDate(),
                    dobMonth: new Date().getMonth() + 1,
                    dobYear: new Date().getFullYear(),
                    gender: "none",
                };
            }

            request.riverty.gender = customerGender.value;
        }

        if (selectedPayMethod.value?.validateIban) {
            request.iban = customerIban.value;
        }

        let response: Response;
        let checkoutStep = CheckoutStepCreateOrder;

        const orderID = cart.value.order_id ?? null
        if (orderID !== null) {
            response = await fetch(`/checkout/update_order?lang=${locale}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(request),
            });
            checkoutStep = CheckoutStepUpdateOrder;
        } else {
            response = await fetch(`/checkout/insert_order?lang=${locale}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(request),
            });
        }

        if (response.status !== 200) {
            const respBody = await response.json();

            if (
                typeof respBody.redirect_url === "string" &&
                respBody.redirect_url !== ""
            ) {
                window.location.href = respBody.redirect_url;
                return false;
            }

            await handleErrorResponseLegacyMethod(respBody);
            return false;
        }

        const respBody = await response.json();
        // Kunnen wij dit event aanpassen/vervangen.
        emitEvent(EventCheckoutStep, {
            step: checkoutStep,
            products: (respBody.products ?? []).map(
                (product: {
                    id: string;
                    name: string;
                    configurationOf: string;
                    price: number;
                    quantity: number;
                    attributes: {
                        [key: string]: {
                            name: string;
                            value: string;
                        };
                    };
                }) => {
                    const attributes: Record<string, string> = {};
                    Object.keys(product.attributes).forEach((key) => {
                        attributes[key] = product.attributes[key].value;
                    });

                    return {
                        ID: product.id ?? (product as any).ID,
                        name: product.name,
                        configurationOf: product.configurationOf ?? (product as any).configuration_of,
                        price: product.price,
                        qty: product.quantity,
                        attributes: attributes,
                    };
                },
            ),
        });

        const url = new URL(generateRoute("checkout_payment"), window.location.href)
        if (respBody.hash !== undefined) {
            url.searchParams.set("hash", respBody.hash);
        }

        window.location.href = url.href;
        return true;
    }

    async function getDefaultTimeFrames() {
        if (!postNLApiOnline.value) {
            return;
        }
        const timeFrames = await getTimeFrames();
        if (timeFrames === null || timeFrames.length === 0) {
            return;
        }

        selectedTimeFrame.value =
            timeFrames.filter((t) => t.option.toLowerCase() === "daytime")[0] ??
            null;
        if (selectedTimeFrame.value === null) {
            selectedTimeFrame.value = timeFrames[0] ?? null;
        }

        const orderID = cart.value.order_id ?? null
        if (
            isPostNLCheckoutMethod() &&
            cart.value.postnl_data === null &&
            selectedTimeFrame.value !== null &&
            orderID === null
        ) {
            await setTimeFrame(selectedTimeFrame.value);
        }
    }

    async function getTimeFrames(): Promise<PostnlTimeFrame[] | null> {
        if (!postNLApiOnline.value) {
            return null;
        }

        let url = `/checkout/postnl/timeframes/default`;
        if (
            invoiceAddress.value.zipcode !== "" &&
            invoiceAddress.value.house_number !== ""
        ) {
            url = `/checkout/postnl/timeframes/${invoiceAddress.value.zipcode.replace(" ", "")}/${invoiceAddress.value.house_number}`;
        } else if (
            shippingAddress.value.zipcode !== "" &&
            shippingAddress.value.house_number !== ""
        ) {
            url = `/checkout/postnl/timeframes/${shippingAddress.value.zipcode.replace(" ", "")}/${shippingAddress.value.house_number}`;
        }

        url = url + `?lang=${locale}`;

        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (response.status !== 200) {
            let error = await response.json();
            if (typeof error.code === "number" && error.code === 1) {
                postNLApiOnline.value = false;
            }

            return null;
        }

        return await response.json();
    }

    async function setTimeFrame(
        timeFrame: PostnlTimeFrame | null = null,
    ): Promise<void> {
        if (!postNLApiOnline.value || timeFrame === null) {
            selectedTimeFrame.value = null;
            return;
        }

        const response = await fetch(
            `/checkout/postnl/set_postnl_timeframe_data?lang=${locale}`,
            {
                method: "POST",
                body: JSON.stringify(timeFrame),
                headers: {
                    "Content-Type": "application/json",
                },
            },
        );
        if (response.status !== 200) {
            await handleErrorResponse(response);
            return;
        }

        selectedTimeFrame.value = timeFrame;
        await getCart();
    }

    async function isPostNLOnline(): Promise<boolean> {
        const response = await fetch(`/checkout/postnl/is_online`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (response.status !== 200) {
            return false;
        }

        const respBody: { isOnline: boolean } = await response.json();
        return respBody?.isOnline ?? false;
    }

    async function getPickupPoints(
        zipcode: string,
        countryCode: "nl" | "be" = "nl",
    ): Promise<PostnlPickupPoint[] | null> {
        if (!postNLApiOnline.value) {
            return null;
        }

        const response = await fetch(
            `/checkout/postnl/nearest_locations/${zipcode}/${countryCode}?lang=${locale}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            },
        );
        if (response.status !== 200) {
            let error = await response.json();
            if (typeof error.code === "number" && error.code === 1) {
                postNLApiOnline.value = false;
            }

            return null;
        }

        const respBody = await response.json();
        return respBody as PostnlPickupPoint[];
    }

    async function setPickupPoint(
        pickupPoint: PostnlPickupPoint,
    ): Promise<boolean> {
        if (!postNLApiOnline.value) {
            return false;
        }

        const response = await fetch(
            `/checkout/postnl/set_postnl_pickup_data?lang=${locale}`,
            {
                method: "POST",
                body: JSON.stringify(pickupPoint),
                headers: {
                    "Content-Type": "application/json",
                },
            },
        );
        if (response.status !== 200) {
            await handleErrorResponse(response);
            return false;
        }

        return true;
    }

    function setPaymentMethod(paymentMethod: CartPaymentMethod | null): void {
        selectedPayMethod.value = paymentMethod;
    }

    async function clearCart(): Promise<boolean> {
        const response = await fetch(`/cart/clear`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (response.status !== 200) {
            await handleErrorResponse(response);
            return false;
        }

        clearLocalStorage()

        return true;
    }

    function clearLocalStorage(): void {
        selectedPayMethod.value = null;
        invoiceAddress.value = emptyAddress();
        sameShippingAddress.value = true;
        shippingAddress.value = emptyAddress();
        orderComment.value = "";
        termsOfServiceAccepted.value = false;
        newsletterAccepted.value = false;
        couponCode.value = "";
        selectedTimeFrame.value = null;
        customerAge.value = new Date();
        customerGender.value = CustomerGenders.None;
        customerIban.value = "";
    }

    function isPostNLCheckoutMethod(): boolean {
        return (
            cart.value.checkout_method?.shipping_method.internal_name.toLowerCase() ===
            "postnl"
        );
    }

    function isPostNLPickupCheckoutMethod(): boolean {
        return (
            cart.value.checkout_method?.shipping_method.internal_name.toLowerCase() ===
            "postnlpickup"
        );
    }

    return {
        // State
        isPopoverOpened: computed(() => isPopoverOpened.value),
        loadingCart: computed(() => loadingCart.value),
        cart: computed(() => cart.value),
        wrappingPaper: computed(() => wrappingPaper.value),
        productCount,
        checkoutCountries: computed(() => checkoutCountries.value),

        giftWrapItems: computed(() => giftWrapItems.value),

        shippingMethods: computed(() => shippingMethods.value),

        paymentMethods: computed(() => paymentMethods.value),

        selectedPayMethod: computed(() => selectedPayMethod.value),

        selectedCountry: computed(() => selectedCountry.value),

        selectedTimeFrame: computed(() => selectedTimeFrame.value),
        isPostNLApiOnline: computed(() => postNLApiOnline.value),

        invoiceAddress: invoiceAddress,
        shippingAddress: shippingAddress,
        sameShippingAddress: sameShippingAddress,
        termsOfServiceAccepted,
        newsletterAccepted,

        orderComment: orderComment,

        couponCode: couponCode,
        age: customerAge,
        gender: customerGender,
        iban: customerIban,

        // Actions
        init: initCart,
        openCart,
        closeCart,
        clearCart,
        clearLocalStorage,

        // Product actions
        add,
        remove,

        // Wrapping paper actions
        setWrappingPaper,
        removeWrappingPaper,

        // Coupon actions
        addCoupon,
        removeCoupon,

        // Country actions
        getCountries,
        setCheckoutCountry,

        // Checkout method actions
        setCheckoutMethod,

        // Address actions
        getAddressAutocomplete,
        saveAddresses,

        // PostNL actions
        getTimeFrames,
        setTimeFrame,
        getPickupPoints,
        setPickupPoint,
        isPostNLCheckoutMethod: computed(() => isPostNLCheckoutMethod()),
        isPostNLPickupCheckoutMethod: computed(() =>
            isPostNLPickupCheckoutMethod(),
        ),

        // Payment actions
        setPaymentMethod,

        createOrder,
    };
});

export const CustomerGenders = {
    None: "none",
    Male: "male",
    Female: "female",
} as const;

export type CustomerGenders =
    (typeof CustomerGenders)[keyof typeof CustomerGenders];

export interface Product {
    ID: string;
    name: string;
    quantity: number;
    price: number;
    original_price: number;
    configuration_of: string | null;
    attributes: Record<
        string,
        {
            name: string;
            value: string;
        }
    >;
    image_url: string;
}

interface Cart {
    products: Product[];
    coupon: CartCoupon | null;
    checkout_method: CartCheckoutMethod | null;
    wrapping_paper: CartWrappingPaper | null;
    postnl_data: PostnlData | null;
    country: CartCountry | null;
    invoice_address: CartAddress | null;
    shipping_address: CartAddress | null;
    order_id: string | null;
    total: number;
}

interface CartCoupon {
    ID: number;
    name: string;
    code: string;
    discount: number;
}

interface CartCheckoutMethod {
    ID: string;
    name: string;
    price: number;
    shipping_method: CartShippingMethod;
    payment_method: CartPaymentMethod;
}

interface CartWrappingPaper {
    ID: string;
    name: string;
    image_url: string;
    price: number;
}

export interface CartCountry {
    ID: string;
    name: string;
    short_code: string;
    is_default: boolean;
}

interface PostnlData {
    type: "postnl" | "postnl-pickup-point";
    pickup_point_address: any;
    data: any;
    price: number;
}

interface PostnlTimeFrame {
    label: string;
    date: string;
    option: string;
    shipping_price: number;
}

interface CartShippingMethod {
    ID: string;
    checkout_method_id: string;
    name: string;
    internal_name: string;
}

export interface CartPaymentMethod {
    checkout_method_id: string;
    internalName: string;
    name: string;
    validateAge: boolean;
    validateGender: boolean;
    validateIban: boolean;
}

export interface CartPaymentMethodOption {
    ID: string;
    name: string;
}

export interface CartAddress {
    first_name: string;
    middle_name: string;
    last_name: string;
    company_name: string;
    city: string;
    street: string;
    house_number: string;
    house_number_addition: string;
    zipcode: string;
    phone_number: string;
    email: string;
}

interface CartAddressAutocomplete {
    city: string;
    house_number: string;
    latitude: number;
    longitude: number;
    province: string;
    province_code: string;
    street: string;
    zipcode: string;
}

interface InsertOrderRequest {
    iban: string;
    internal_name: string;
    checkoutMethod: string;
    riverty: InsertOrderRivertyData | null;
    subscribeNewsletter: boolean;
    orderComment: string;
}

interface InsertOrderRivertyData {
    dobDay: number;
    dobMonth: number;
    dobYear: number;
    gender: "male" | "female" | "none";
}

interface PostnlPickupPoint {
    city: string;
    country_code: string;
    house_number: string;
    house_number_extension: string;
    remark: string;
    street: string;
    zipcode: string;
    distance: number;
    delivery_options: string[];
    latitude: number;
    longitude: number;
    location_code: string;
    name: string;
    opening_hours: OpeningHours;
    partner_name: string;
    phone_number: string;
    retail_network_id: string;
    saleschannel: string;
    terminal_type: string;
}

interface OpeningHours {
    monday: string[];
    tuesday: string[];
    wednesday: string[];
    thursday: string[];
    friday: string[];
    saturday: string[];
    sunday: string[];
}
